import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getOnlyNRoomLeftLabel } from 'lib/offer/getUrgencyLabels'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getOfferExtra } from 'selectors/offerExtraSelector'
import Group from 'components/utils/Group'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextLink from 'components/Luxkit/TextLink'
import TextButton from 'components/Luxkit/Button/TextButton'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import LineExclamationCircleIcon from 'components/Luxkit/Icons/line/LineExclamationCircleIcon'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import useIsAvailableForPurchase from '../../hooks/useIsAvailableForPurchase'
import AccommodationRoomTile from '../../Accommodation/AccommodationRoomTile'
import AccommodationRoomTileImage from '../../Accommodation/AccommodationRoomTileImage'
import AccommodationRoomTileDetailsVariant from './AccommodationRoomTileDetailsVariant'
import FlashHotelRateOptionVariant from './FlashHotelRateOptionVariant'

const AccommodationPackageTileContainer = styled.div`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface Props {
  offer: App.Offer;
  bookedToday?: number;
  packageViews: Array<OfferPackageOptionView>;
  isLuxPlusEnabled: boolean;
}

function FlashHotelRoomOptionVariant(props: Props) {
  const {
    offer,
    bookedToday,
    packageViews,
    isLuxPlusEnabled,
  } = props

  const pkg = packageViews[0].package

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const cheapestPackageView = useMemo(() => packageViews.reduce((cheapest, current) => current.hotelPrice < cheapest.hotelPrice ? current : cheapest), [packageViews])
  const availableForPurchase = useIsAvailableForPurchase()

  const memberPrice = cheapestPackageView.baseMemberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const luxPlusSaveAmount = showMemberPrice ? cheapestPackageView.basePrice - memberPrice : undefined

  const roomsLeftLabel = getOnlyNRoomLeftLabel((cheapestPackageView.availableRooms ?? 0), true, config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD)

  const offerPageDispatch = useOfferPageDispatch()

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const handleOpenRoomDetails = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
  }, [offerPageDispatch, pkg])

  if (!pkg.roomType) { return null }

  return (
    <Pane bordered data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile>
        <AccommodationRoomTileImage
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetailsVariant
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            {luxPlusSaveAmount && offer.luxPlus.access !== 'memberOnly' && <LuxPlusLabelWithModal type="member-price-offer" offer={offer} saveAmount={luxPlusSaveAmount}/>}
            {roomsLeftLabel && <UrgencyLabel variant="critical2">{roomsLeftLabel}</UrgencyLabel>}
            {(bookedToday ?? 0) >= 3 && <UrgencyLabel variant="critical1" icon={<SolidBoltAltIcon size="XS" />}>Booked {bookedToday} times today</UrgencyLabel>}
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        />
      </AccommodationRoomTile>
      <AccommodationPackageTileContainer>
        {datesAvailable && roomFitsCapacity && (
          <FlashHotelRateOptionVariant
            key={cheapestPackageView.package.uniqueKey}
            offer={offer}
            packageView={cheapestPackageView}
          />
        )}
        {(!datesAvailable || !roomFitsCapacity) && (
          <Group direction="vertical" gap={8}>
            <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room is sold out on your dates or max number of guests exceeded"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink></>}
            />
            <TextButton kind="secondary" size="medium" onClick={handleOpenRoomDetails}>
              View details
            </TextButton>
          </Group>
        )}
      </AccommodationPackageTileContainer>
    </Pane>
  )
}

export default withOfferPageAndReduxState(
  (state, appState) => ({
    isLuxPlusEnabled: isLuxPlusEnabled(appState),
    offerExtra: getOfferExtra(appState, state.offer?.id),
  }),
)(FlashHotelRoomOptionVariant)
